import { EnvironmentProviders, importProvidersFrom, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions, withNoIncrementalHydration } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { TosService } from '@libs/account/components';
import { ConfirmationDialogComponent, MaintenanceComponent, NoPermissionDialogComponent, SnackbarComponent, SpinnerComponent } from '@libs/common/components';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppUpdateDialogFormComponent } from '@libs/components/cep';
import { CmsActionLinkHandler } from '@libs/cms/components';
import { provideDocumentsSummary } from '@libs/services/components';
import { AuthService, AuthServiceFactory, NO_PERMISSION_DIALOG_COMPONENT_TYPE } from '@libs/shared/auth';
import { liveUpdateInterceptor } from '@libs/shared/live-update';
import {
  APP_UPDATE_DIALOG_COMPONENT_TYPE,
  AppStateService,
  ENVIRONMENT,
  EnvironmentModel,
  // eslint-disable-next-line no-restricted-imports
  FeatureFlagsService,
  FLASH_MESSAGE_COMPONENT_TYPE,
  MAINTENANCE_COMPONENT_TYPE,
  SPINNER_OVERLAY_COMPONENT_TYPE,
  ErrorHandlerModule,
} from '@libs/shared/common';
import {
  AppStateEffects,
  appStateReducer,
  CartStateEffects,
  cartStateReducer,
  CONFIRMATION_DIALOG_COMPONENT_TYPE,
} from '@libs/shared/services';
import { PageInfoTrackingService } from '@libs/shared/tracking';
import { ActionLinkHandler, MatConstantsModule } from '@libs/shared/ui';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

export const provideCep = (production: boolean): EnvironmentProviders =>
  makeEnvironmentProviders([
    ...AuthServiceFactory.createProviders(),
    provideHttpClient(withFetch(), withInterceptorsFromDi(), withInterceptors([liveUpdateInterceptor])),
    provideClientHydration(
      withEventReplay(),
      // Keep the SSR-resolved content language after hydration.
      withHttpTransferCacheOptions({ includeHeaders: ['Content-Language'] }),
      withNoIncrementalHydration(),
    ),
    importProvidersFrom(
      BrowserAnimationsModule,
      MatConstantsModule,
      ErrorHandlerModule,
      StoreModule.forRoot({ app: appStateReducer, cart: cartStateReducer }),
      EffectsModule.forRoot([AppStateEffects, CartStateEffects]),
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: production }),
    ),
    {
      provide: ActionLinkHandler,
      useClass: CmsActionLinkHandler,
    },
    provideDocumentsSummary(),
    {
      provide: SPINNER_OVERLAY_COMPONENT_TYPE,
      useValue: SpinnerComponent,
    },
    {
      provide: FLASH_MESSAGE_COMPONENT_TYPE,
      useValue: SnackbarComponent,
    },
    {
      provide: APP_UPDATE_DIALOG_COMPONENT_TYPE,
      useValue: AppUpdateDialogFormComponent,
    },
    {
      provide: CONFIRMATION_DIALOG_COMPONENT_TYPE,
      useValue: ConfirmationDialogComponent,
    },
    {
      provide: MAINTENANCE_COMPONENT_TYPE,
      useValue: MaintenanceComponent,
    },
    {
      provide: NO_PERMISSION_DIALOG_COMPONENT_TYPE,
      useValue: NoPermissionDialogComponent,
    },
  ]);

export const provideCepBrowserEnvironment = (environment: EnvironmentModel): EnvironmentProviders =>
  makeEnvironmentProviders([
    {
      provide: ENVIRONMENT,
      useFactory: (): EnvironmentModel => {
        const baseHref: string = Capacitor.isNativePlatform() ? '' : environment.app?.baseHref ?? '';

        return {
          ...environment,
          app: {
            ...environment.app,
            baseHref,
          },
        };
      },
    },
  ]);

export const provideCepHostInitializer = (environment: EnvironmentModel): EnvironmentProviders =>
  makeEnvironmentProviders([
    provideAppInitializer(() => {
      const flags: FeatureFlagsService = inject(FeatureFlagsService);

      flags.enableNewAppConfig();

      if (environment.app?.context === 'public') {
        flags.enablePublicApp();
      }

      const appState: AppStateService = inject(AppStateService);

      appState.set('hidePageNavigation', true);

      const tosService: TosService = inject(TosService);

      tosService.initialize();

      const pageInfoTrackingService: PageInfoTrackingService = inject(PageInfoTrackingService);

      pageInfoTrackingService.init();

      return inject(AuthService).checkAuthState();
    }),
  ]);
